import { Button } from '@pets-ui/Button/Button'
import { ButtonToggleVisiblePassword } from '@pets-ui/ButtonToggleVisiblePassword/ButtonToggleVisiblePassword'
import { HStack } from '@pets-ui/HStack/HStack'
import { ArrowLeftSmall, PasswordSuccess as PasswordSuccessIcon } from '@pets-ui/icons'
import { Input } from '@pets-ui/Input/Input'
import { SvgIcon } from '@pets-ui/SvgIcon/SvgIcon'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { useWpLayout } from '@pets-ui/useWpLayout/useWpLayout'
import { VStack } from '@pets-ui/VStack/VStack'
import { useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { useRequestSetPassword } from '../../../hooks/apis/wp/useRequestSetPassword'
import { InputVerify } from '../../InputVerify'
import { useContextCosign } from '../../useContextCosign'
import { CardLogin } from '../CardLogin'
import { CardTitleWithIcon } from '../login/CardTitleWithIcon'
import { Service } from '../Service'
import { validationPassword } from './validationPassword'

export function PasswordReset() {
  const { t } = useTranslation()
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [error, setError] = useState<Record<string, string>>()
  const { passwordErrors, isSame, passwordError, confirmPasswordError } = validationPassword({ password, confirmPassword })
  // NOTE: https://app.shortcut.com/dogcatstar/story/38329/cosign-logout
  const [successReset, setSuccessReset] = useState(false)
  const { trigger, isMutating } = useRequestSetPassword()
  const { title } = useContextCosign()
  useWpLayout({ footer: false })

  const handleClickResetPassword = async () => {
    try {
      await trigger({ password })
      setSuccessReset(true)
    }
    catch (e) {
      console.error(e)
      setError(error)
    }
  }

  const handleClickBack = () => {
    window.location.pathname = 'my-account'
  }

  const hasEmpty = [password, confirmPassword].some(item => item.trim() === '')
  const canSubmit = isSame && !passwordError && !hasEmpty

  return (
    <CardLogin>
      <Helmet>
        <title>
          {t('login.resetPassword')}
          {' '}
          -
          {' '}
          {title}
        </title>
      </Helmet>
      {successReset
        ? (
          <VStack gap={4} alignItems="center">
            <SvgIcon fontSize={120} color={colors.green400}><PasswordSuccessIcon /></SvgIcon>
            <Text variant="h4">{t('login.passwordSetSuccess')}</Text>
            <Button onClick={handleClickBack} fullWidth>{t('login.returnToLogin')}</Button>
          </VStack>
        )
        : (
          <>
            <CardTitleWithIcon
              title={t('login.resetPassword')}
              leftIcon={<ArrowLeftSmall />}
              onClickLeft={handleClickBack}
            />
            <VStack gap={4}>
              <VStack gap={1}>
                <Input
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  label={t('login.enterPassword')}
                  placeholder={String(t('login.pleaseEnter'))}
                  fullWidth
                  type={showPassword ? 'text' : 'password'}
                  endAdornment={<ButtonToggleVisiblePassword show={showPassword} onClick={() => setShowPassword(!showPassword)} />}
                  error={!!error?.password}
                />
                <HStack gap={3}>
                  <InputVerify success={passwordErrors.characters} rule={t('login.leastEightCharacters')} />
                  <InputVerify success={passwordErrors.englishLetter} rule={t('login.oneLetter')} />
                  <InputVerify success={passwordErrors.numberLetter} rule={t('logib.oneNumber')} />
                </HStack>
              </VStack>
              <VStack gap={1}>
                <Input
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  label={t('login.confirmPassword')}
                  placeholder={String(t('login.reEnterPassword'))}
                  fullWidth
                  type={showConfirmPassword ? 'text' : 'password'}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleClickResetPassword()
                    }
                  }}
                  endAdornment={<ButtonToggleVisiblePassword show={showConfirmPassword} onClick={() => setShowConfirmPassword(!showConfirmPassword)} />}
                  error={confirmPasswordError}
                  helpText={confirmPasswordError ? t('login.passwordsNotMatch') : ''}
                />
              </VStack>
              <Button
                onClick={handleClickResetPassword}
                disabled={!canSubmit}
                loading={isMutating}
              >
                {t('login.setPassword')}
              </Button>
            </VStack>
          </>
        )}
      <Service />
    </CardLogin>
  )
}
